import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface ProductSpecification {
  id: string;
  product_id: string;
  product_category: string;
  firewall_users: number | null;
  ngfw_users: number | null;
  firewall_throughput_udp: number | null;
  firewall_throughput_imix: number | null;
  ips_throughput_udp: number | null;
  ips_throughput_imix: number | null;
  vpn_throughput_aes: number | null;
  vpn_throughput_aes_imix: number | null;
  threat_prevention_throughput: number | null;
  concurrent_connections: number | null;
  new_connections_per_second: number | null;
  max_rules: number | null;
  max_vpn_ipsec_tunnels: number | null;
  max_vpn_ssl_tunnels: number | null;
  max_static_routes: number | null;
  max_qos_queues: number | null;
  max_logical_interfaces: number | null;
  high_availability: boolean;
  redundant_power_supply: string | null;
  redundant_disk: boolean;
  copper_interfaces_1g: string | null;
  copper_interfaces_2_5g: string | null;
  fiber_interfaces_1g: string | null;
  fiber_interfaces_10g: string | null;
  fiber_interfaces_25g: string | null;
  fiber_interfaces_40g: string | null;
  initial_storage: string | null;
  form_factor: string | null;
  tpm_chipset: boolean;
  ip_protection_code: string | null;
  din_rail: boolean;
  fan_less: boolean;
  industrial_protocols: string | null;
  products?: {
    id: string;
    slug: string;
    is_active: boolean;
  };
}

export interface ProductSpecWithTranslation extends ProductSpecification {
  productName: string;
  productSlug: string;
}

export function useProductSpecs(productCategory?: 'infrastructure' | 'industrial') {
  return useQuery({
    queryKey: ['product-specs', productCategory],
    queryFn: async () => {
      let query = supabase
        .from('product_specifications')
        .select(`
          *,
          products!inner(id, slug, is_active)
        `)
        .eq('products.is_active', true)
        .order('firewall_users', { ascending: true });

      if (productCategory) {
        query = query.eq('product_category', productCategory);
      }

      const { data, error } = await query;
      
      if (error) throw error;
      return data as ProductSpecification[];
    }
  });
}

export function useProductSpecBySlug(slug: string) {
  return useQuery({
    queryKey: ['product-spec', slug],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('product_specifications')
        .select(`
          *,
          products!inner(id, slug, is_active)
        `)
        .eq('products.slug', slug)
        .eq('products.is_active', true)
        .maybeSingle();
      
      if (error) throw error;
      return data as ProductSpecification | null;
    },
    enabled: !!slug
  });
}
